import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";

import Layout from "../../../../layout";
import BackButton from "../../../../components/BackButton";
import { uploadFile } from "../../../../utils/helpers";

import {
  checkEditorialPermissionAction,
  publishEditorialAction,
  getBroadcastStatusAction,
} from "../../../../redux/actions/feedCardAction";

import "./writeArticle.css";

/**
 * WriteArticle — page where assigned users can write and publish
 * editorial articles to the Workbrook tab.
 *
 * Only users with profiles.can_create_editorial = 1 (or admin IDs)
 * can access this page. The permission is checked on mount.
 *
 * Uses Quill for the rich text editor (same as admin app).
 */
const WriteArticle = () => {
  const history = useHistory();
  const quillRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const fileInputRef = useRef(null);
  const featuredInputRef = useRef(null);

  const [checking, setChecking] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [broadcastFlag, setBroadcastFlag] = useState(false);
  const [featuredImage, setFeaturedImage] = useState("");
  const [featuredPreview, setFeaturedPreview] = useState("");
  const [broadcastStatus, setBroadcastStatus] = useState(null);

  // Check permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      const result = await checkEditorialPermissionAction();
      setHasPermission(result.can_create);
      setChecking(false);
    };
    checkPermission();
  }, []);

  // Fetch broadcast usage/limit status on mount so the checkbox reflects
  // whether a broadcast can actually be sent right now.
  useEffect(() => {
    if (!hasPermission) return;
    const fetchBroadcastStatus = async () => {
      const result = await getBroadcastStatusAction();
      setBroadcastStatus(result);
    };
    fetchBroadcastStatus();
  }, [hasPermission]);

  // Load Quill dynamically
  useEffect(() => {
    if (!hasPermission || quillInstanceRef.current) return;

    const loadQuill = async () => {
      // Load CSS
      if (!document.querySelector('link[href*="quill.snow.css"]')) {
        const link = document.createElement("link");
        link.href = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }

      // Load JS
      if (!window.Quill) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Initialize Quill
      if (quillRef.current && window.Quill) {
        quillInstanceRef.current = new window.Quill(quillRef.current, {
          theme: "snow",
          placeholder: "Write your article here...",
          modules: {
            toolbar: {
              container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ color: [] }, { background: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ indent: "-1" }, { indent: "+1" }],
                [{ align: [] }],
                ["link", "image", "blockquote", "code-block"],
                ["clean"],
              ],
              handlers: {
                image: handleImageUpload,
              },
            },
          },
        });
      }
    };

    loadQuill();
  }, [hasPermission]);

  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/png,image/jpeg,image/gif,image/webp");
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;

      setIsUploading(true);
      Promise.resolve(
        uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        )
      )
        .then(({ data, success }) => {
          if (success && quillInstanceRef.current) {
            const range = quillInstanceRef.current.getSelection(true);
            quillInstanceRef.current.insertEmbed(
              range.index,
              "image",
              data,
              window.Quill.sources.USER
            );
            quillInstanceRef.current.setSelection(
              range.index + 1,
              window.Quill.sources.SILENT
            );
          }
        })
        .catch(() => {
          cogoToast.error("Image upload failed", {
            position: "top-center",
            hideAfter: 3,
          });
        })
        .finally(() => {
          setIsUploading(false);
        });
    };
  };

  const handleFeaturedImage = ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => setFeaturedPreview(e.target.result);
      reader.readAsDataURL(file);

      setIsUploading(true);
      Promise.resolve(
        uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        )
      )
        .then(({ data, success }) => {
          if (success) {
            setFeaturedImage(data);
          } else {
            cogoToast.error("Image upload failed", {
              position: "top-center",
              hideAfter: 3,
            });
            setFeaturedPreview("");
          }
        })
        .finally(() => {
          setIsUploading(false);
        });
    }
  };

  const removeFeaturedImage = () => {
    setFeaturedImage("");
    setFeaturedPreview("");
    if (featuredInputRef.current) featuredInputRef.current.value = "";
  };

  const handlePublish = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      cogoToast.error("Please enter a headline", {
        position: "top-center",
        hideAfter: 3,
      });
      return;
    }

    if (!category) {
      cogoToast.error("Please select a category", {
        position: "top-center",
        hideAfter: 3,
      });
      return;
    }

    const articleBody = quillInstanceRef.current
      ? quillInstanceRef.current.root.innerHTML
      : "";

    if (!articleBody || articleBody === "<p><br></p>") {
      cogoToast.error("Please write some content", {
        position: "top-center",
        hideAfter: 3,
      });
      return;
    }

    // Pre-check broadcast limit before publishing — re-fetch the latest
    // status in case it changed since page load (e.g. another admin
    // just sent a broadcast, or reset the limit).
    if (broadcastFlag) {
      const latestStatus = await getBroadcastStatusAction();
      setBroadcastStatus(latestStatus);
      if (latestStatus.success && latestStatus.limit_reached) {
        cogoToast.error(
          `Monthly broadcast limit reached (${latestStatus.sent_this_month}/${latestStatus.monthly_limit}). Ask an admin to reset the limit or increase it.`,
          { position: "top-center", hideAfter: 5 }
        );
        return;
      }
    }

    setPublishing(true);

    const payload = {
      title: title.trim(),
      article_body: articleBody,
      category,
      preview_text: previewText.trim() || undefined,
      canonical_url: canonicalUrl.trim() || undefined,
      broadcast_flag: broadcastFlag,
      image_url: featuredImage || undefined,
    };

    const result = await publishEditorialAction(payload);

    setPublishing(false);

    if (result.success) {
      cogoToast.success("Article published successfully!", {
        position: "top-center",
        hideAfter: 4,
      });
      history.push("/league");
    } else {
      cogoToast.error(result.message || "Failed to publish article", {
        position: "top-center",
        hideAfter: 4,
      });
    }
  };

  // Loading state
  if (checking) {
    return (
      <Layout>
        <div className="write-article-loading">
          <div className="write-article-spinner" />
          <p>Checking permissions...</p>
        </div>
      </Layout>
    );
  }

  // No permission
  if (!hasPermission) {
    return (
      <Layout>
        <div className="write-article-denied">
          <div className="write-article-denied-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
          </div>
          <h2>Access Denied</h2>
          <p>You don't have permission to create editorial content.</p>
          <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "8px" }}>
            Only users assigned by the Workbrook admin can write articles.
          </p>
          <button
            onClick={() => history.push("/league")}
            className="write-article-back-btn"
          >
            Back to Feed
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="write-article-page">
        <div className="write-article-container">
          {/* Header */}
          <div className="write-article-header">
            <div>
              <BackButton />
              <h1>Write an Article</h1>
              <p>Share career insights, hiring trends, or industry news with the Workbrook community.</p>
            </div>
          </div>

          <form onSubmit={handlePublish} className="write-article-form">
            {/* Main content */}
            <div className="write-article-main">
              {/* Headline */}
              <div className="write-article-field">
                <label>Headline <span className="write-article-required">*</span></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter article headline..."
                  className="write-article-input"
                  maxLength={255}
                />
              </div>

              {/* Preview text */}
              <div className="write-article-field">
                <label>Preview text <span className="write-article-optional">(optional)</span></label>
                <textarea
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Short summary shown on the feed card. Auto-generated if left blank."
                  className="write-article-textarea"
                  rows={2}
                  maxLength={300}
                />
              </div>

              {/* Quill editor */}
              <div className="write-article-field">
                <label>Article content <span className="write-article-required">*</span></label>
                <div ref={quillRef} className="write-article-editor" />
                {isUploading && (
                  <div className="write-article-upload-status">
                    <div className="write-article-spinner write-article-spinner-sm" />
                    <span>Uploading image...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="write-article-sidebar">
              {/* Category */}
              <div className="write-article-panel">
                <h3>Category</h3>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="write-article-select"
                >
                  <option value="">— Select category —</option>
                  <option value="Career Advice">Career Advice</option>
                  <option value="Hiring Insights">Hiring Insights</option>
                  <option value="Industry Insights">Industry Insights</option>
                  <option value="Product Update">Product Update</option>
                </select>
              </div>

              {/* Featured image */}
              <div className="write-article-panel">
                <h3>Featured Image</h3>
                {featuredPreview && (
                  <div className="write-article-image-preview">
                    <img src={featuredPreview} alt="Preview" />
                    <button type="button" onClick={removeFeaturedImage} className="write-article-image-remove">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                )}
                <input
                  ref={featuredInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFeaturedImage}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={() => featuredInputRef.current?.click()}
                  className="write-article-upload-btn"
                  disabled={isUploading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  {isUploading ? "Uploading..." : "Upload Image"}
                </button>
              </div>

              {/* Canonical URL */}
              <div className="write-article-panel">
                <h3>Canonical URL <span className="write-article-optional">(optional)</span></h3>
                <input
                  type="url"
                  value={canonicalUrl}
                  onChange={(e) => setCanonicalUrl(e.target.value)}
                  placeholder="https://workbrook.com/blog/..."
                  className="write-article-input write-article-input-sm"
                />
                <p className="write-article-hint">
                  If this article is also published on the blog, enter the blog URL here for SEO.
                </p>
              </div>

              {/* Broadcast */}
              <div className="write-article-panel">
                <h3>Broadcast</h3>
                {broadcastStatus && (
                  <p
                    className="write-article-hint"
                    style={{
                      color: broadcastStatus.limit_reached ? "#dc2626" : "#6b7280",
                      fontWeight: broadcastStatus.limit_reached ? 600 : 400,
                    }}
                  >
                    {broadcastStatus.limit_reached
                      ? `Monthly limit reached (${broadcastStatus.sent_this_month}/${broadcastStatus.monthly_limit}). Ask an admin to reset or raise the limit.`
                      : `${broadcastStatus.sent_this_month}/${broadcastStatus.monthly_limit} broadcasts used this month.`}
                  </p>
                )}
                <label
                  className="write-article-checkbox"
                  style={broadcastStatus?.limit_reached ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <input
                    type="checkbox"
                    checked={broadcastFlag}
                    disabled={!!broadcastStatus?.limit_reached}
                    onChange={(e) => setBroadcastFlag(e.target.checked)}
                  />
                  <span>
                    <strong>Flag as broadcast</strong>
                    <small>Triggers an email to all users. Reserve for major announcements only.</small>
                  </span>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="write-article-publish-btn"
                disabled={publishing || isUploading}
              >
                {publishing ? (
                  <>
                    <div className="write-article-spinner write-article-spinner-sm" />
                    Publishing...
                  </>
                ) : (
                  "Publish to Feed"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default WriteArticle;
